import axios from 'axios'
import { createRouter, createWebHistory } from 'vue-router'
import store from '@/store'
import { isAuthRejection } from '@/store/modules/auth'
import authService from '@/services/auth'
import { requestReauth, installSessionInterceptor } from '@signifi/vision-shell/session'
import { ACTIVE_APP_STORAGE_KEY, appsFor, defaultRouteFor } from '@/config/navigation'

// If the user has a remembered email, resolve their IDP and redirect directly
// to it without showing the login page. Returns true if a navigation was
// initiated (caller must not call next()), false if the login page should be
// used instead.
async function autoRedirectOrLogin(redirect) {
  const savedEmail = localStorage.getItem('vc_login_email');
  if (savedEmail) {
    try {
      const { domain } = await authService.resolveProvider(savedEmail);
      const relayState = JSON.stringify({ domain, url: window.location.origin + redirect });
      const ssoUrl = new URL('/api/auth/sso', window.location.origin);
      ssoUrl.searchParams.set('domain', domain);
      ssoUrl.searchParams.set('RelayState', relayState);
      window.location.assign(ssoUrl.toString());
      return true;
    } catch {
      // Domain no longer configured — fall through to the login page
    }
  }
  return false;
}

let lastSessionCheck = 0;
const SESSION_CACHE_MS = 2 * 60 * 1000; // re-validate every 2 minutes

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      component: () => import('../pages/LoginPage.vue')
    },
    {
      // Where the popup sign-in flow lands after re-authentication. Outside the
      // application shell: its whole job is to hand the fresh session to the tabs
      // that are waiting and close itself.
      path: '/auth/resumed',
      component: () => import('@signifi/vision-shell').then((shell) => shell.AuthResumed)
    },
    {
      // Outside the application shell: this is a wall display, so the URL opened
      // on a television fills the screen rather than giving a fifth of it to a
      // navigation drawer nobody is going to click. Authentication and the
      // permission check still apply — the guard is global.
      path: '/ops/mission-control',
      meta: {
        permission: 'ops.view'
      },
      component: () => import('../pages/OpsMissionControlPage.vue')
    },
    {
      path: '/',
      component: () => import('../layouts/AppLayout.vue'),
      children: [
        {
          // Resolved by the guard once the session is known — see `meta.landing`.
          path: '/',
          meta: {
            landing: true
          },
          component: () => import('../pages/LandingPage.vue')
        },
        {
          path: '/ops',
          meta: {
            permission: 'ops.view'
          },
          component: () => import('../pages/OpsHomePage.vue')
        },
        {
          path: '/ops/overview',
          meta: {
            permission: 'ops.view'
          },
          component: () => import('../pages/OpsOverviewPage.vue')
        },
        {
          path: '/ops/glance',
          meta: {
            permission: 'ops.view'
          },
          component: () => import('../pages/OpsGlancePage.vue')
        },
        {
          path: '/ops/week',
          meta: {
            permission: 'ops.view'
          },
          component: () => import('../pages/OpsWeekPage.vue')
        },
        {
          path: '/ops/calendar',
          meta: {
            permission: 'ops.view'
          },
          component: () => import('../pages/OpsCalendarPage.vue')
        },
        {
          path: '/ops/briefing',
          meta: {
            permission: 'ops.view'
          },
          component: () => import('../pages/OpsBriefingPage.vue')
        },
        // The check-in cycle: one department's week, and the board a project manager
        // reads every department's answers from. Both are readable by anyone in Ops;
        // whether a department may be written is the API's answer, per department.
        {
          path: '/ops/checkin',
          meta: {
            permission: 'ops.view'
          },
          component: () => import('../pages/OpsCheckinPage.vue')
        },
        {
          path: '/ops/checkin-board',
          meta: {
            permission: 'ops.view'
          },
          component: () => import('../pages/OpsCheckinBoardPage.vue')
        },
        {
          path: '/ops/readiness',
          meta: {
            permission: 'ops.view'
          },
          component: () => import('../pages/OpsReadinessPage.vue')
        },
        {
          path: '/ops/capacity',
          meta: {
            permission: 'ops.view'
          },
          component: () => import('../pages/OpsCapacityPage.vue')
        },
        // The two analytic views. Both only read, and both read the snapshot the
        // boards render, so both take the key that reveals Ops.
        {
          path: '/ops/pipeline',
          meta: {
            permission: 'ops.view'
          },
          component: () => import('../pages/OpsPipelinePage.vue')
        },
        {
          path: '/ops/insights',
          meta: {
            permission: 'ops.view'
          },
          component: () => import('../pages/OpsInsightsPage.vue')
        },
        {
          path: '/ops/activity',
          meta: {
            permission: 'ops.view'
          },
          component: () => import('../pages/OpsActivityLogPage.vue')
        },
        // Reading the administered lists takes no more than seeing Ops; changing one
        // takes `ops.settings.admin`, checked in the page and again at the API.
        {
          path: '/ops/settings',
          meta: {
            permission: 'ops.view'
          },
          component: () => import('../pages/OpsSettingsPage.vue')
        },
        {
          path: '/ops/procurement',
          meta: {
            permission: 'ops.view'
          },
          component: () => import('../pages/OpsProcurementPage.vue')
        },
        {
          path: '/ops/production',
          meta: {
            permission: 'ops.view'
          },
          component: () => import('../pages/OpsProductionPage.vue')
        },
        {
          path: '/ops/qa',
          meta: {
            permission: 'ops.view'
          },
          component: () => import('../pages/OpsQaPage.vue')
        },
        // The two department queues are one working-table page over the queue's
        // own declaration; `queue` is what tells it which one it is showing.
        {
          path: '/ops/shipping',
          meta: {
            permission: 'ops.view',
            queue: 'shipping'
          },
          component: () => import('../pages/OpsWorkingSchedulePage.vue')
        },
        {
          path: '/ops/installation',
          meta: {
            permission: 'ops.view',
            queue: 'installation'
          },
          component: () => import('../pages/OpsWorkingSchedulePage.vue')
        },
        // The three department schedules are one page over the schedule's own
        // declaration; `schedule` is what tells it which one it is showing.
        {
          path: '/ops/schedules/vinyl',
          meta: {
            permission: 'ops.view',
            schedule: 'vinyl'
          },
          component: () => import('../pages/OpsSchedulePage.vue')
        },
        {
          path: '/ops/schedules/planogram',
          meta: {
            permission: 'ops.view',
            schedule: 'planogram'
          },
          component: () => import('../pages/OpsSchedulePage.vue')
        },
        {
          path: '/ops/schedules/software',
          meta: {
            permission: 'ops.view',
            schedule: 'software'
          },
          component: () => import('../pages/OpsSchedulePage.vue')
        },
        // UAT is readable by anyone in Ops: a project manager needs to see what was
        // tested without being able to answer a checklist. The write keys are checked
        // per action inside the pages and again at the API.
        {
          path: '/ops/uat',
          meta: {
            permission: 'ops.view'
          },
          component: () => import('../pages/OpsUatPage.vue')
        },
        {
          path: '/ops/uat/checklists',
          meta: {
            permission: 'ops.view'
          },
          component: () => import('../pages/OpsUatChecklistsPage.vue')
        },
        {
          path: '/ops/uat/sessions/:sessionId',
          meta: {
            permission: 'ops.view'
          },
          component: () => import('../pages/OpsUatSessionPage.vue')
        },
        // The document workspace is readable by anyone in Ops — the readiness list
        // is a report on the record. Producing a document takes
        // `ops.documents.generate`, checked in the page and again at the API.
        {
          path: '/ops/documents',
          meta: {
            permission: 'ops.view'
          },
          component: () => import('../pages/OpsDocumentsPage.vue')
        },
        {
          path: '/ops/projects',
          meta: {
            permission: 'ops.view'
          },
          component: () => import('../pages/OpsProjectsPage.vue')
        },
        {
          path: '/ops/customers',
          meta: {
            permission: 'ops.view'
          },
          component: () => import('../pages/OpsCustomerPage.vue')
        },
        {
          path: '/ops/solutions/workload',
          meta: {
            permission: 'ops.view'
          },
          component: () => import('../pages/OpsSaWorkloadPage.vue')
        },
        {
          path: '/ops/in-life',
          meta: {
            permission: 'ops.view'
          },
          component: () => import('../pages/OpsInLifePage.vue')
        },
        {
          path: '/ops/projects/:id',
          meta: {
            permission: 'ops.view'
          },
          component: () => import('../pages/OpsProjectPage.vue')
        },
        {
          path: '/ops/intake',
          meta: {
            permission: 'ops.projects.create'
          },
          component: () => import('../pages/OpsIntakePage.vue')
        },
        {
          path: '/ops/templates',
          meta: {
            permission: 'ops.settings.admin'
          },
          component: () => import('../pages/OpsTemplatesPage.vue')
        },
        {
          path: '/ops/document-templates',
          meta: {
            permission: 'ops.settings.admin'
          },
          component: () => import('../pages/OpsDocumentTemplatesPage.vue')
        },
        {
          path: '/ops/reconciliation',
          meta: {
            permission: 'ops.settings.admin'
          },
          component: () => import('../pages/OpsContactReconciliationPage.vue')
        },
        {
          path: '/locations',
          meta: {
            permission: 'kiosks.view',
            dashboardKey: 'locations'
          },
          component: () => import('../pages/DashboardPage.vue')
        },
        {
          path: '/kiosks',
          meta: {
            permission: 'kiosks.view',
            dashboardKey: 'kiosks'
          },
          component: () => import('../pages/DashboardPage.vue')
        },
        {
          path: '/kiosks/:portalId/:kioskId',
          meta: {
            permission: 'kiosks.view'
          },
          component: () => import('../pages/KioskDetailPage.vue')
        },
        {
          path: '/reports/uptime',
          meta: {
            permission: 'kiosks.view',
            dashboardKey: 'fleet-uptime'
          },
          component: () => import('../pages/DashboardPage.vue')
        },
        {
          path: '/reports/vending',
          meta: {
            permission: 'transactions.view',
            dashboardKey: 'vending'
          },
          component: () => import('../pages/DashboardPage.vue')
        },
        {
          path: '/dashboards',
          meta: {
            permission: 'dashboards.view'
          },
          component: () => import('../pages/DashboardsPage.vue')
        },
        {
          path: '/dashboards/builder/:key?',
          meta: {
            permission: 'dashboards.view'
          },
          component: () => import('../pages/DashboardBuilderPage.vue')
        },
        {
          path: '/planograms',
          meta: {
            permission: 'planogram.view'
          },
          component: () => import('../pages/PlanogramsPage.vue')
        },
        {
          path: '/planograms/editor/:key?',
          meta: {
            permission: 'planogram.view'
          },
          component: () => import('../pages/PlanogramEditorPage.vue')
        },
        {
          path: '/event-log',
          component: () => import('../pages/AlertsPage.vue')
        },
        {
          path: '/users',
          meta: {
            permission: 'users.view'
          },
          component: () => import('../pages/UsersPage.vue')
        },
        {
          path: '/roles',
          meta: {
            permission: 'roles.view'
          },
          component: () => import('../pages/RolesPage.vue')
        },
        {
          path: '/scopes',
          meta: {
            permission: 'scopes.view'
          },
          component: () => import('../pages/ScopesPage.vue')
        },
        {
          path: '/api-keys',
          meta: {
            permission: 'apikeys.view'
          },
          component: () => import('../pages/ApiKeysPage.vue')
        },
        {
          path: '/proton-workers',
          meta: {
            permission: 'proton.view'
          },
          component: () => import('../pages/ProtonWorkersPage.vue')
        },
        {
          path: '/contacts',
          meta: {
            permission: 'contacts.view'
          },
          component: () => import('../pages/ContactsPage.vue')
        },
        {
          path: '/connect',
          meta: {
            permission: 'connect.view'
          },
          component: () => import('../pages/ConnectFlowsPage.vue')
        },
        {
          path: '/connect/flows/:id',
          meta: {
            permission: 'connect.view'
          },
          component: () => import('../pages/ConnectEditorPage.vue')
        },
        {
          path: '/connect/flows/:id/pipeline',
          meta: {
            permission: 'connect.view'
          },
          component: () => import('../pages/ConnectPipelinePage.vue')
        },
        {
          path: '/servers',
          meta: {
            permission: 'servers.view'
          },
          component: () => import('../pages/ServersPage.vue')
        },
        {
          path: '/dbadmin/databases',
          meta: {
            permission: 'dbadmin.databases.view'
          },
          component: () => import('../pages/DbAdminDatabasesPage.vue')
        },
        {
          path: '/dbadmin/query/:databaseId',
          meta: {
            permission: 'dbadmin.databases.view'
          },
          component: () => import('../pages/DbAdminQueryPage.vue')
        },
        {
          path: '/dbadmin/backups/:databaseId',
          meta: {
            permission: 'dbadmin.databases.view'
          },
          component: () => import('../pages/DbAdminBackupsPage.vue')
        },
        {
          path: '/dbadmin/imports',
          meta: {
            permission: 'dbadmin.databases.view'
          },
          component: () => import('../pages/DbAdminImportsPage.vue')
        },
        {
          path: '/dbadmin/audit',
          meta: {
            permission: 'dbadmin.audit.view'
          },
          component: () => import('../pages/DbAdminAuditPage.vue')
        },
        {
          path: '/portals',
          meta: {
            permission: 'portals.view',
            dashboardKey: 'portals'
          },
          component: () => import('../pages/DashboardPage.vue')
        },
        {
          path: '/releases',
          meta: {
            permission: 'releases.view'
          },
          component: () => import('../pages/ReleasesPage.vue')
        },
        {
          path: '/integrations',
          meta: {
            permission: 'projects.view'
          },
          component: () => import('../pages/IntegrationsPage.vue')
        },
        // Signifi SHIELD. Reading the findings takes the key that reveals the
        // application; the assets and the policy behind them are administered
        // under their own key, because they decide what every scan run means.
        {
          path: '/shield',
          redirect: '/shield/findings'
        },
        {
          path: '/shield/findings',
          meta: {
            permission: 'shield.view'
          },
          component: () => import('../pages/ShieldFindingsPage.vue')
        },
        {
          path: '/shield/findings/:id',
          meta: {
            permission: 'shield.view'
          },
          component: () => import('../pages/ShieldFindingPage.vue')
        },
        {
          // Posture over time, rendered by the shared dashboard engine from a
          // seeded config. The document is served by SHIELD rather than by
          // `/api/dashboards`, which asks for `dashboards.view` — the fleet's
          // reporting grant, which says nothing about security work.
          path: '/shield/posture',
          meta: {
            permission: 'shield.view',
            dashboardKey: 'shield-posture',
            dashboardApi: '/api/shield/dashboards/'
          },
          component: () => import('../pages/DashboardPage.vue')
        },
        {
          // The three queues a decision chain is worked from. Behind the key
          // that reveals the application rather than behind either decision
          // permission: a proposer with neither still needs to see where their
          // own proposals have got to.
          path: '/shield/decisions',
          meta: {
            permission: 'shield.view'
          },
          component: () => import('../pages/ShieldDecisionsPage.vue')
        },
        {
          // The exceptions register: everything the ledger has stopped counting.
          // Behind the reading key rather than the confirming one — it is the
          // report an external reviewer is shown, and acting on a row is gated
          // where the act is, not where the list is.
          path: '/shield/exceptions',
          meta: {
            permission: 'shield.view'
          },
          component: () => import('../pages/ShieldExceptionsPage.vue')
        },
        {
          path: '/shield/runs',
          meta: {
            permission: 'shield.view'
          },
          component: () => import('../pages/ShieldRunsPage.vue')
        },
        {
          path: '/shield/settings',
          meta: {
            permission: 'shield.settings.admin'
          },
          component: () => import('../pages/ShieldSettingsPage.vue')
        },
        {
          // The cross-cutting audit trail. Its own key rather than the reading
          // or the administering one: an auditor granted administration to see
          // the log would be handed the ability to change what it records.
          path: '/shield/audit',
          meta: {
            permission: 'shield.audit.view'
          },
          component: () => import('../pages/ShieldAuditPage.vue')
        },
        // Audience Tracking. The two report pages are dashboard documents like
        // Fleet Uptime, so the deployment's own seed decides what they hold and
        // reading one also takes `dashboards.view` at the API; the device list
        // is the fleet behind them. `audience.view` is what reveals all three.
        {
          path: '/audience',
          redirect: '/audience/overview'
        },
        {
          path: '/audience/overview',
          meta: {
            permission: 'audience.view',
            dashboardKey: 'audience-overview'
          },
          component: () => import('../pages/DashboardPage.vue')
        },
        {
          path: '/audience/footfall',
          meta: {
            permission: 'audience.view',
            dashboardKey: 'audience-footfall'
          },
          component: () => import('../pages/DashboardPage.vue')
        },
        {
          path: '/audience/devices',
          meta: {
            permission: 'audience.view'
          },
          component: () => import('../pages/AudienceDevicesPage.vue')
        },
        {
          path: '/forbidden',
          component: () => import('../pages/ForbiddenPage.vue')
        }
      ]
    }
  ]
})

router.beforeEach(async (to, from, next) => {
  if (to.path === '/login') {
    // Already authenticated — send straight to the app
    if (store.getters['auth/isLoggedIn']) {
      return next('/');
    }
    return next();
  }

  const now = Date.now();
  if (!store.getters['auth/isLoggedIn'] || (now - lastSessionCheck > SESSION_CACHE_MS)) {
    try {
      await store.dispatch('auth/getActiveSession');
      lastSessionCheck = Date.now();
    } catch (err) {
      // Stamp the check either way. A failed check still counts as "checked", which is
      // what keeps a persistently unreachable backend from being re-hit on every
      // navigation: the cache window above gates the next attempt.
      lastSessionCheck = Date.now();
      if (isAuthRejection(err)) {
        // The server refused the session. A mounted layout means the current page may be
        // holding unsaved work, so the navigation is cancelled in favor of the in-place
        // re-auth prompt — a redirect here would unload the SPA and discard that work.
        // With nothing mounted (a fresh tab, the wall display) there is nothing to lose,
        // so re-authenticate the way this guard always has.
        if (store.getters['auth/isLoggedIn'] && requestReauth()) {
          return next(false);
        }
        lastSessionCheck = 0;
        const redirect = to.path.startsWith('/api/') ? '/' : to.fullPath;
        const redirected = await autoRedirectOrLogin(redirect);
        return redirected ? next() : next('/login?redirect=' + encodeURIComponent(redirect));
      }
      if (!store.getters['auth/isLoggedIn']) {
        // The server never answered and there is no held session to ride on — the login
        // page is the only place left to go.
        return next('/login?redirect=' + encodeURIComponent(to.fullPath));
      }
      // The server never answered, but the session we already hold is untouched — let the
      // navigation through on it rather than force a re-login over a blip. The permission
      // check below runs against that cached session; the API authorizes every request
      // independently, so a stale permission here cannot widen what the user can read.
    }
  }

  // Permissions are checked on every navigation, not only the ones that
  // refreshed the session — otherwise a route entered inside the cache window
  // is admitted unchecked.
  const permissions = store.getters['auth/session'].permissions || [];

  // The bare root resolves to the app the browser was last in, falling back to
  // the first app the session can reach, so the landing page is never one the
  // user is not permitted to open.
  if (to.meta.landing) {
    const reachable = appsFor(permissions);
    const stored = localStorage.getItem(ACTIVE_APP_STORAGE_KEY);
    const appId = reachable.some(app => app.id === stored) ? stored : (reachable[0] || {}).id;
    const target = appId ? defaultRouteFor(appId, permissions) : null;
    return next(target || '/forbidden');
  }

  if (to.meta.permission && !permissions.includes(to.meta.permission)) {
    return next('/forbidden');
  }

  return next();
})

// A 401 while the shell is mounted re-authenticates in place: whatever request just failed
// was very possibly carrying unsaved work, and navigating away would be the one way to
// truly lose it. The shell parks the request rather than failing it and replays it once the
// user signs back in. With no shell mounted (the login page, the wall display) there is no
// prompt to offer, so a remembered email goes straight to its identity provider and anyone
// else to the login page. The session probe and the login page itself are left alone.
installSessionInterceptor(axios, {
  onUnhosted: async () => {
    const redirect = window.location.pathname + window.location.search;
    const redirected = await autoRedirectOrLogin(redirect);
    if (!redirected) {
      window.location.assign('/login?redirect=' + encodeURIComponent(redirect));
    }
  }
})

export default router
